/**
 * The v1 component set (SPEC §4.2, plus WorldSettings and PostProcess: A-09, A-10).
 *
 * Components are data only: no methods, no behavior. Systems read them (the
 * TransformSystem, render translators, v2 game modes). `display.order` sets
 * Inspector section order, and the lowest-order component with an
 * `outlinerType` labels the entity in the Outliner.
 *
 * Units: meters, degrees, Y-up (A-07). Colors: 0..1 sRGB or `{ theme }` tokens.
 */
import { SHAPE_ASSET_KIND } from "../../assets/engineAssets";
import { ComponentRegistry } from "../schema/ComponentRegistry";
import { defineComponent } from "../schema/defineComponent";
import type { ComponentSchema } from "../schema/defineComponent";

// ─── Spatial ─────────────────────────────────────────────────────────────

export const Transform = defineComponent({
  type: "Transform",
  category: "Transform",
  display: { order: 0 },
  rules: { required: true },
  props: {
    position: { type: "vec3", label: "Location", step: 0.1, unit: "m" },
    rotation: { type: "vec3", label: "Rotation", step: 1, unit: "°" },
    scale: { type: "vec3", label: "Scale", step: 0.1, default: [1, 1, 1] },
  },
});

// ─── Level ───────────────────────────────────────────────────────────────

export const WorldSettings = defineComponent({
  type: "WorldSettings",
  category: "World Settings",
  display: { order: 5, outlinerType: "Level", icon: "level" },
  rules: { required: true, perLevel: true },
  props: {
    environment: {
      type: "enum",
      label: "Environment",
      options: [{ value: "studio-dusk", label: "Studio Dusk" }],
    },
    // Off for a level that brings its own ground (an environment such as a map).
    floor: { type: "bool", label: "Studio floor", default: true },
    startupCamera: { type: "entityRef", label: "Startup camera", requires: "Camera" },
  },
});

export const Zone = defineComponent({
  type: "Zone",
  category: "Zone",
  display: { order: 30, outlinerType: "Zone", icon: "zone" },
  props: {
    camera: { type: "entityRef", label: "Camera", requires: "Camera" },
    gameMode: { type: "string", label: "Game mode" },
    streamOnLoad: { type: "bool", label: "Stream on load", default: true },
  },
});

// ─── Content (bound to content.seed.json through $ref) ──────────────────

export const Role = defineComponent({
  type: "Role",
  category: "Role",
  display: { order: 20 },
  ignoredKeys: ["id"],
  props: {
    title: { type: "string", label: "Title" },
    company: { type: "string", label: "Company" },
    team: { type: "string", label: "Team", optional: true },
    scope: { type: "text", label: "Scope", optional: true },
    start: { type: "yearMonth", label: "Dates", pairWith: "end" },
    end: { type: "yearMonth", label: "End", nullable: true, nullLabel: "Present" },
    location: { type: "string", label: "Location" },
    highlights: { type: "array", label: "Highlights", of: { type: "string" } },
  },
});

export const Project = defineComponent({
  type: "Project",
  category: "Project",
  display: { order: 20 },
  ignoredKeys: ["level", "links"],
  props: {
    name: { type: "string", label: "Name" },
    tagline: { type: "string", label: "Tagline" },
    summary: { type: "text", label: "Summary" },
    highlights: { type: "array", label: "Highlights", of: { type: "string" } },
  },
});

/** A school's degree, on an education level's zone: what its world panel shows. */
export const Degree = defineComponent({
  type: "Degree",
  category: "Degree",
  display: { order: 20 },
  ignoredKeys: ["id"],
  props: {
    degree: { type: "string", label: "Degree" },
    school: { type: "string", label: "School" },
    concentration: { type: "string", label: "Concentration" },
    years: { type: "string", label: "Years" },
    highlights: { type: "array", label: "Highlights", of: { type: "string" } },
  },
});

export const Link = defineComponent({
  type: "Link",
  category: "Link",
  display: { order: 40, outlinerType: "Link", icon: "link" },
  props: {
    label: { type: "string", label: "Label" },
    url: { type: "string", label: "URL" },
  },
});

// ─── UI (Unity-style: a Canvas and UI element children) ───────────────────

/**
 * A world-space UI surface, like Unity's world-space Canvas: its UI element
 * children (Text, Image) are drawn onto it, stacked top to bottom in Outliner
 * order, as a panel in the scene.
 */
export const Canvas = defineComponent({
  type: "Canvas",
  category: "Canvas",
  display: { order: 34, outlinerType: "UI", icon: "panel" },
  rules: { toggleable: true },
  props: {
    width: { type: "number", label: "Width", min: 0.2, max: 6, step: 0.1, unit: "m", default: 1.6 },
    height: {
      type: "number",
      label: "Height",
      min: 0.2,
      max: 4,
      step: 0.1,
      unit: "m",
      default: 0.9,
    },
    background: { type: "bool", label: "Background", default: true },
  },
});

/** UI text on the parent Canvas. Blank lines start new paragraphs. */
export const Text = defineComponent({
  type: "Text",
  category: "Text",
  display: { order: 36, outlinerType: "Text", icon: "text" },
  rules: { parentNeedsOneOf: ["Canvas"], toggleable: true },
  props: {
    text: { type: "text", label: "Text" },
    style: {
      type: "enum",
      label: "Style",
      options: [
        { value: "body", label: "Body" },
        { value: "title", label: "Title" },
        { value: "meta", label: "Meta" },
        { value: "eyebrow", label: "Eyebrow" },
      ],
    },
  },
});

/** A UI image on the parent Canvas, as wide as the canvas allows and this tall. */
export const Image = defineComponent({
  type: "Image",
  category: "Image",
  display: { order: 37, outlinerType: "Image", icon: "image" },
  rules: { parentNeedsOneOf: ["Canvas"], toggleable: true },
  props: {
    image: { type: "assetRef", label: "Image", kinds: ["texture"] },
    height: {
      type: "number",
      label: "Height",
      min: 16,
      max: 1200,
      step: 4,
      unit: "px",
      default: 160,
    },
  },
});

// ─── Rendering ───────────────────────────────────────────────────────────

/** Mesh Renderer props that apply only while the mesh is an engine shape. */
const SHAPES = { assetProp: "mesh", kind: SHAPE_ASSET_KIND } as const;
const MODELS = { assetProp: "mesh", kind: "gltf" } as const;

export const MeshRenderer = defineComponent({
  type: "MeshRenderer",
  category: "Mesh Renderer",
  display: { order: 50, outlinerType: "Mesh", icon: "mesh" },
  rules: { toggleable: true },
  props: {
    /**
     * An engine shape or a glTF model (champion models, SPEC §7): one field,
     * as Unreal's Static Mesh and Unity's Mesh Filter. None draws nothing.
     */
    mesh: { type: "assetRef", label: "Mesh", kinds: [SHAPE_ASSET_KIND, "gltf"] },
    // A model brings its own size and materials; these apply to engine shapes.
    size: {
      type: "vec3",
      label: "Size",
      step: 0.1,
      unit: "m",
      default: [1, 1, 1],
      shownWhen: SHAPES,
    },
    material: {
      type: "enum",
      label: "Material",
      options: [
        { value: "platform", label: "Platform" },
        { value: "standard", label: "Standard" },
      ],
      shownWhen: SHAPES,
    },
    color: { type: "color", label: "Base color", shownWhen: SHAPES },
    castShadow: { type: "bool", label: "Cast shadow", default: true },
    /**
     * Streams the model after the level is revealed, at low network priority,
     * as Unreal streams a non-blocking level: for a large environment, so the
     * level opens with its characters and the map arrives behind them.
     */
    background: { type: "bool", label: "Load in background", shownWhen: MODELS },
  },
});

/**
 * Plays a clip of the entity's MeshRenderer model. Mirrors the Animation
 * section of Unreal's skeletal mesh component in "Use Animation Asset" mode:
 * one clip, no blending. Frames only advance while the viewport is Realtime.
 */
export const Animator = defineComponent({
  type: "Animator",
  category: "Animation",
  display: { order: 52 },
  rules: { requires: ["MeshRenderer"], toggleable: true },
  props: {
    /** A clip name in the model's glTF; empty plays the first clip. */
    clip: {
      type: "clipRef",
      label: "Anim to play",
      source: { component: "MeshRenderer", prop: "mesh" },
    },
    playing: { type: "bool", label: "Playing", default: true },
    loop: { type: "bool", label: "Looping", default: true },
    speed: { type: "number", label: "Play rate", min: 0, max: 4, step: 0.05, default: 1 },
  },
});

export const Placeholder = defineComponent({
  type: "Placeholder",
  category: "Placeholder",
  display: { order: 45, outlinerType: "Mesh", icon: "mesh" },
  rules: { toggleable: true },
  props: {
    shape: {
      type: "enum",
      label: "Shape",
      options: [
        { value: "capsule", label: "Capsule" },
        { value: "box", label: "Box" },
        { value: "plane", label: "Plane" },
      ],
    },
    height: {
      type: "number",
      label: "Height",
      min: 0.1,
      max: 10,
      step: 0.1,
      unit: "m",
      default: 1.8,
    },
    radius: {
      type: "number",
      label: "Radius",
      min: 0.05,
      max: 5,
      step: 0.05,
      unit: "m",
      default: 0.35,
    },
    /** Viewport label shown under the figure, e.g. `[Champion_Hero]`. */
    label: { type: "string", label: "Label" },
  },
});

export const Light = defineComponent({
  type: "Light",
  category: "Light",
  display: { order: 55, outlinerType: "Light", icon: "light" },
  rules: { toggleable: true },
  props: {
    kind: {
      type: "enum",
      label: "Type",
      options: [
        { value: "directional", label: "Directional" },
        { value: "point", label: "Point" },
        { value: "spot", label: "Spot" },
      ],
    },
    color: { type: "color", label: "Color" },
    intensity: { type: "number", label: "Intensity", min: 0, max: 100, step: 0.1, default: 1 },
    range: { type: "number", label: "Range", min: 0, max: 100, step: 0.5, unit: "m", default: 10 },
    angle: {
      type: "number",
      label: "Cone angle",
      min: 1,
      max: 90,
      step: 1,
      unit: "°",
      default: 35,
    },
    castShadow: { type: "bool", label: "Cast shadow" },
  },
});

export const Camera = defineComponent({
  type: "Camera",
  category: "Camera",
  display: { order: 60, outlinerType: "Cam", icon: "camera" },
  rules: { toggleable: true },
  props: {
    fov: {
      type: "number",
      label: "Field of view",
      min: 10,
      max: 120,
      step: 1,
      unit: "°",
      default: 40,
    },
  },
});

export const VFX = defineComponent({
  type: "VFX",
  category: "VFX",
  display: { order: 65, outlinerType: "FX", icon: "fx" },
  rules: { toggleable: true },
  props: {
    effect: {
      type: "enum",
      label: "Effect",
      options: [{ value: "emberRing", label: "Ember ring" }],
    },
    radius: {
      type: "number",
      label: "Radius",
      min: 0.1,
      max: 20,
      step: 0.1,
      unit: "m",
      default: 2.4,
    },
    count: { type: "number", label: "Particles", min: 0, max: 2000, step: 1, default: 160 },
    color: { type: "color", label: "Color", default: { theme: "vfx" } },
  },
});

export const PostProcess = defineComponent({
  type: "PostProcess",
  category: "Post Process",
  display: { order: 70, outlinerType: "Post", icon: "post" },
  rules: { perLevel: true, toggleable: true },
  props: {
    exposure: { type: "number", label: "Exposure", min: 0.1, max: 4, step: 0.05, default: 1 },
    bloomStrength: {
      type: "number",
      label: "Bloom strength",
      min: 0,
      max: 3,
      step: 0.05,
      default: 0.6,
    },
    bloomThreshold: {
      type: "number",
      label: "Bloom threshold",
      min: 0,
      max: 2,
      step: 0.05,
      default: 0.8,
    },
    tonemapper: {
      type: "enum",
      label: "Tonemapper",
      options: [
        { value: "AgX", label: "AgX" },
        { value: "ACES", label: "ACES Filmic" },
      ],
    },
  },
});

/** Every component registered by the World module. */
/**
 * The level's sky: a panorama drawn behind everything, as Unreal's sky sphere
 * or HDRI backdrop and Godot's WorldEnvironment sky. The site's theme picks
 * the variant, Day in light themes and Night in dark ones, as it already sets
 * the studio's colors and tints the lights. Like a post-process volume, the
 * first visible one wins, and hiding it brings back the studio gradient.
 */
export const Sky = defineComponent({
  type: "Sky",
  category: "Sky",
  display: { order: 68, outlinerType: "Sky", icon: "sky" },
  rules: { perLevel: true, toggleable: true },
  props: {
    /** Equirectangular panoramas (2:1). */
    day: { type: "assetRef", label: "Day", kinds: ["texture"] },
    night: { type: "assetRef", label: "Night", kinds: ["texture"] },
    /** Turns the panorama about the vertical, to place its sun or moon. */
    rotation: { type: "number", label: "Rotation", min: 0, max: 360, step: 1, unit: "°" },
  },
});

/**
 * The level's fog, as Unreal's Exponential Height Fog actor: the air between
 * the camera and the scene, not a post-process (it's drawn per pixel in the
 * scene pass). The site's theme picks the density, as it picks the Sky's
 * panorama: a bright day haze reads far thicker than a dark night one at the
 * same density. Like a post-process volume, the first visible one wins; with
 * none, or hidden, there's no fog.
 */
export const Fog = defineComponent({
  type: "Fog",
  category: "Fog",
  display: { order: 69, outlinerType: "Fog", icon: "fog" },
  rules: { perLevel: true, toggleable: true },
  props: {
    /** Exponential-squared density per meter. */
    dayDensity: {
      type: "number",
      label: "Day density",
      min: 0,
      max: 0.2,
      step: 0.001,
      default: 0.02,
    },
    nightDensity: {
      type: "number",
      label: "Night density",
      min: 0,
      max: 0.2,
      step: 0.001,
      default: 0.02,
    },
    /**
     * Distant ground fades into the Sky behind it (its haze just below the
     * horizon) rather than into the studio's horizon color.
     */
    colorFromSky: { type: "bool", label: "Color from sky", default: true },
  },
});

export const V1_COMPONENTS: readonly ComponentSchema[] = [
  Transform,
  WorldSettings,
  Zone,
  Role,
  Project,
  Degree,
  Link,
  Canvas,
  Text,
  Image,
  MeshRenderer,
  Animator,
  Placeholder,
  Light,
  Camera,
  VFX,
  PostProcess,
  Sky,
  Fog,
];

/** A registry with every v1 component registered: what the World module boots with. */
export function createV1ComponentRegistry(): ComponentRegistry {
  const registry = new ComponentRegistry();
  V1_COMPONENTS.forEach((schema) => {
    registry.register(schema);
  });
  return registry;
}
